import { EntityProcessingErrorsPanel } from '@agilelab/plugin-wb-builder-catalog';
import { WitboostSystem } from '@agilelab/plugin-wb-builder-common';
import {
  createRefreshEntityFunction,
  customAlertApiRef,
  CustomView,
  EnumFilter,
  useRefreshPermission,
  WbButton,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  WbDrawer,
  WbCardActions,
  WbDrawerContent,
} from '@agilelab/plugin-wb-platform';
import { practiceShaperImportPermission } from '@agilelab/plugin-wb-rbac-common';
import { configApiRef, useApi } from '@mainstage/core-plugin-api';
import { stringifyEntityRef } from '@mainstage/catalog-model';
import { catalogApiRef, EntityProvider } from '@mainstage/plugin-catalog-react';
import { usePermission } from '@mainstage/plugin-permission-react';
import {
  makeStyles,
  Box,
  Paper,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { WbTooltip } from '@agilelab/plugin-wb-platform';
import CachedIcon from '@material-ui/icons/Cached';
import { useMemo } from 'react';
import pluralize from 'pluralize';
import 'reactflow/dist/style.css';
import { usePracticeShaperGraph } from '../../context/PracticeShaperGraphContext';
import { Legend } from './Legend';
import { PracticesShaperGraph } from './PracticeShaperGraph';
import { TeamRolesSettingsCard } from './TeamRolesSettingsCard';
import { hasOneOfTeamRoles } from '@agilelab/plugin-wb-practice-shaper-common';

const PAGE_HEIGHT_MINUS_CONTENT = 96 + 40 + 24;

const useStyles = makeStyles(
  theme => ({
    paper: {
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        overflow: 'visible',
      },
    },
    dockableGroup: {
      minHeight: 0,
      minWidth: 0,
      overflow: 'hidden',
      // instead of filling the container, it becomes height auto on small screens
      [theme.breakpoints.down('sm')]: {
        height: 'auto',
        minHeight: 400,
        overflow: 'visible',
      },
    },
    designPanelContent: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      minWidth: 0,
    },
    landscapeFilter: {
      padding: theme.spacing(1.5),
      paddingBottom: 0,
      width: 'fit-content',
    },
    graphWrapper: {
      flex: '1 1 0',
      minHeight: 0,
      minWidth: 0,
      [theme.breakpoints.down('sm')]: {
        minHeight: 400,
      },
    },
  }),
  { name: 'PracticeShaperOverview' },
);

export function PracticeShaperOverview({
  importPageRef,
}: {
  importPageRef: string;
}) {
  const {
    selectedEntity,
    setSelectedEntity,
    selectedTaxonomyRefs,
    setSelectedTaxonomyRefs,
    data: { refetch, isFetching, data: graphData },
  } = usePracticeShaperGraph();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const configApi = useApi(configApiRef);

  const { allowed } = usePermission({
    permission: practiceShaperImportPermission,
  });

  const { allowRefresh } = useRefreshPermission(selectedEntity);

  const refreshEntity = createRefreshEntityFunction({
    entity: selectedEntity,
    catalogApi,
    alertApi,
  });

  const label = configApi.getString('practiceShaper.taxonomy.label');
  const pluralizedLabel =
    configApi.getOptionalString('practiceShaper.taxonomy.pluralizedLabel') ??
    pluralize(label);

  const taxonomyOptions = useMemo(
    () => graphData?.taxonomies.map(t => stringifyEntityRef(t)) ?? [],
    [graphData?.taxonomies],
  );

  const taxonomyDisplayNames = useMemo(() => {
    const map = new Map<string, string>();
    graphData?.taxonomies.forEach(t => {
      map.set(
        stringifyEntityRef(t),
        (t.metadata as any).displayName || t.metadata.name,
      );
    });
    return map;
  }, [graphData?.taxonomies]);

  const entityName = selectedEntity
    ? (selectedEntity.metadata as any).displayName ||
      selectedEntity.metadata.name
    : '';

  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Paper className={classes.paper}>
        <WbDockableGroup
          className={`${classes.dockableGroup}`}
          direction="horizontal"
          style={
            isSmallScreen
              ? undefined
              : {
                  height: `calc(100vh - ${PAGE_HEIGHT_MINUS_CONTENT}px)`,
                }
          }
        >
          <WbDockablePanel
            minSize={15}
            defaultSize={30}
            title="Legend"
            headerBorderBottom
          >
            <Legend />
          </WbDockablePanel>
          <WbDockableResizeHandle direction="horizontal" />
          <WbDockablePanel
            defaultSize={70}
            title="Design"
            headerBorderBottom
            headerActions={
              <WbCardActions breakpoint="w_xs">
                <WbTooltip title="Refresh the Practice Shaper Entities">
                  <span>
                    <WbButton
                      variant="outlined"
                      onClick={() => refetch()}
                      label="Refresh"
                      icon={<CachedIcon />}
                      disabled={isFetching}
                      fullWidthUnderBreakpoint="w_xs"
                    />
                  </span>
                </WbTooltip>
                {allowed ? (
                  <WbTooltip title="Register a new Practice Shaper entity">
                    <span>
                      <WbButton
                        label="Register new entity"
                        to={importPageRef}
                        fullWidthUnderBreakpoint="w_xs"
                      />
                    </span>
                  </WbTooltip>
                ) : undefined}
              </WbCardActions>
            }
          >
            <Box className={classes.designPanelContent}>
              {graphData && (
                <Box className={classes.landscapeFilter}>
                  <EnumFilter<string>
                    field={pluralizedLabel}
                    options={taxonomyOptions}
                    value={
                      selectedTaxonomyRefs.length > 0
                        ? selectedTaxonomyRefs
                        : undefined
                    }
                    renderOption={ref => taxonomyDisplayNames.get(ref) ?? ref}
                    renderValue={ref => taxonomyDisplayNames.get(ref) ?? ref}
                    onChange={value => setSelectedTaxonomyRefs(value ?? [])}
                    onSearch={search =>
                      taxonomyOptions.filter(ref =>
                        new RegExp(search, 'ig').test(
                          taxonomyDisplayNames.get(ref) ?? ref,
                        ),
                      )
                    }
                  />
                </Box>
              )}
              <Box className={classes.graphWrapper}>
                <PracticesShaperGraph />
              </Box>
            </Box>
          </WbDockablePanel>
        </WbDockableGroup>
        <EntityProvider entity={selectedEntity ? selectedEntity : undefined}>
          <WbDrawer
            open={!!selectedEntity}
            setOpen={() => setSelectedEntity(null)}
            title={entityName}
          >
            {selectedEntity && (
              <WbDrawerContent
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <EntityProcessingErrorsPanel />
                <CustomView
                  id="practice_shaper_content"
                  transform="builder_system"
                  typeId={selectedEntity.kind}
                  data={{
                    entity: selectedEntity as WitboostSystem,
                    allowRefresh,
                  }}
                  actions={{ refreshEntity: refreshEntity }}
                />
                {hasOneOfTeamRoles(selectedEntity) && (
                  <TeamRolesSettingsCard systemType={selectedEntity} />
                )}
              </WbDrawerContent>
            )}
          </WbDrawer>
        </EntityProvider>
      </Paper>
    </>
  );
}
